/** Read-only interpretation of the historical public-trial notes contract.
 * Unrecognised content is retained; this module never rewrites stored notes.
 */
export const QUESTIONNAIRE_SECTIONS = ['personal', 'emergency', 'goal', 'training', 'ems', 'motivation'] as const;
export type QuestionnaireSection = typeof QUESTIONNAIRE_SECTIONS[number];
export type QuestionnaireField = 'age' | 'gender' | 'height' | 'weight' | 'clothingSize' | 'emergencyPhone' | 'goal' | 'currentlyTraining' | 'trainingType' | 'trainingFrequency' | 'lastTrained' | 'availability' | 'triedEms' | 'emsFeedback' | 'motivation' | 'limitations';
export type MedicalTopic = 'general' | 'cardiovascular' | 'injuries' | 'treatment';
export interface MedicalResponse { topic: MedicalTopic; answer: boolean | null; details: string }
export interface ClientQuestionnaire {
  date: string | null;
  fields: Partial<Record<QuestionnaireField, string>>;
  medical: MedicalResponse[];
  additional: string;
}
export interface QuestionnaireSummary { questionnaires: ClientQuestionnaire[]; additional: string }

const HEADER = /(?:Formulaire d'essai du ([^\n]+)\r?\n)?EMS TRIAL BOOKING FORM \(online submission\)/g;
const missing = (value: string) => value.trim() === '-' || value.trim() === '';
const boolean = (value: string): boolean | null => value.trim() === 'Yes' ? true : value.trim() === 'No' ? false : null;
export const questionnaireBoolean = boolean;

export function parseClientQuestionnaires(notes: string | null | undefined): QuestionnaireSummary {
  if (!notes) return { questionnaires: [], additional: '' };
  const markers = [...notes.matchAll(HEADER)];
  if (!markers.length) return { questionnaires: [], additional: notes };
  const questionnaires = markers.map((marker, index): ClientQuestionnaire => {
    const start = (marker.index ?? 0) + marker[0].length;
    const end = markers[index + 1]?.index ?? notes.length;
    const fields: ClientQuestionnaire['fields'] = {};
    const medical: MedicalResponse[] = [];
    const additional: string[] = [];
    let continuation: 'motivation' | 'emsFeedback' | 'limitations' | MedicalTopic | null = null;
    const put = (key: QuestionnaireField, value: string) => {
      if (!missing(value)) fields[key] = value.trim();
    };
    for (const raw of notes.slice(start, end).replace(/\r/g, '').split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      const safety = line.match(/^(general|cardiovascular|injuries|treatment):\s*(Yes|No)(?:\s*—\s*(.*))?$/);
      if (safety) {
        const topic = safety[1] as MedicalTopic;
        medical.push({ topic, answer: boolean(safety[2]), details: (safety[3] ?? '').split(/\s*↵\s*/).join('\n') });
        continuation = topic;
        continue;
      }
      const emergency = line.match(/^Emergency phone:\s*(.*)$/);
      if (emergency) { put('emergencyPhone', emergency[1]); continuation = null; continue; }
      const entry = line.match(/^(Profile|Main goal|Currently training|Availability|Tried EMS before|Limitations|Motivation):\s*(.*)$/);
      if (entry) {
        continuation = null;
        const [, label, value] = entry;
        if (label === 'Profile') {
          const profile = value.match(/^height ([\d.]+) cm, weight ([\d.]+) kg, size ([\w]+)(?:, age (\d+))?(?:, (male|female))?$/);
          if (profile) {
            put('height', profile[1]); put('weight', profile[2]); put('clothingSize', profile[3]);
            if (profile[4]) put('age', profile[4]);
            if (profile[5]) put('gender', profile[5]);
          } else additional.push(raw);
        } else if (label === 'Main goal') put('goal', value);
        else if (label === 'Currently training') {
          const [answer, ...parts] = value.split(/\s*\|\s*/);
          put('currentlyTraining', answer);
          for (const part of parts) {
            if (part.startsWith('type: ')) put('trainingType', part.slice(6));
            else if (part.endsWith('x per week')) put('trainingFrequency', part.slice(0, -10).trim());
            else if (part.startsWith('last trained: ')) put('lastTrained', part.slice(14));
            else additional.push(part);
          }
        } else if (label === 'Availability') {
          const match = value.match(/^(.*?) session\(s\) per week$/);
          if (match) put('availability', match[1]); else additional.push(raw);
        } else if (label === 'Tried EMS before') {
          const [answer, ...details] = value.split(/\s*\|\s*/);
          put('triedEms', answer); put('emsFeedback', details.join(' | '));
          continuation = 'emsFeedback';
        } else if (label === 'Limitations') {
          const [answer, ...details] = value.split(/\s*\|\s*/);
          put('limitations', answer);
          const detail = details.join(' | ');
          if (detail.startsWith('Emergency phone:')) put('emergencyPhone', detail.slice(16));
          else if (detail) fields.limitations = `${answer} — ${detail}`;
          continuation = 'limitations';
        } else if (label === 'Motivation') { put('motivation', value); continuation = 'motivation'; }
        continue;
      }
      // Labelled unknown lines are never swallowed into a recognised answer.
      if (continuation && !/^[^:]+:/.test(line)) {
        const medicalEntry = medical.find(item => item.topic === continuation);
        if (medicalEntry) medicalEntry.details += `${medicalEntry.details ? '\n' : ''}${raw}`;
        else if (continuation === 'motivation' || continuation === 'emsFeedback' || continuation === 'limitations') {
          fields[continuation] = `${fields[continuation] ?? ''}\n${raw}`.trim();
        }
      } else { additional.push(raw); continuation = null; }
    }
    return { date: marker[1]?.trim() ?? null, fields, medical, additional: additional.join('\n') };
  });
  return { questionnaires, additional: notes.slice(0, markers[0].index ?? 0).trim() };
}